/**
 * Build time-series rows for pose charts from pose-analysis samples.
 * Racket arm angle uses shoulder–elbow–wrist (stored as left/right_elbow).
 * Shoulder metric = signed horizontal delta (racket shoulder x − other shoulder x)
 * in *display* space (matches the CSS-oriented overlay the coach sees).
 *
 * Horizontal mirror (flip_h / 180°) confuses MediaPipe L/R on the analysis
 * frame — racket arm mapping is swapped when the view mirrors X.
 */

import { mapStorageToDisplayNorm } from "./videoOrientCoords";

/** COCO-17 indices. */
const L_SHOULDER = 5;
const R_SHOULDER = 6;
const L_ELBOW = 7;
const R_ELBOW = 8;
const L_WRIST = 9;
const R_WRIST = 10;
const L_HIP = 11;
const R_HIP = 12;
const L_KNEE = 13;
const R_KNEE = 14;

function numOrNull(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function jointOk(j, minScore = 0.2) {
  return (
    j &&
    Number.isFinite(Number(j.x)) &&
    Number.isFinite(Number(j.y)) &&
    (j.score == null || Number(j.score) >= minScore)
  );
}

function jointOkRelaxed(j, minScore = 0.12) {
  return jointOk(j, minScore);
}

/**
 * Signed oriented angle at vertex b for a–b–c (degrees, −180…180).
 * Positive = counterclockwise from ba→bc in the coordinate system used.
 */
function signedAngleDegreesAt(a, b, c) {
  const v1x = Number(a.x) - Number(b.x);
  const v1y = Number(a.y) - Number(b.y);
  const v2x = Number(c.x) - Number(b.x);
  const v2y = Number(c.y) - Number(b.y);
  if (Math.hypot(v1x, v1y) * Math.hypot(v2x, v2y) < 1e-5) return null;
  const cross = v1x * v2y - v1y * v2x;
  const dot = v1x * v2x + v1y * v2y;
  return Math.round((Math.atan2(cross, dot) * 180) / Math.PI);
}

function orientationSteps(viewSetup) {
  if (!viewSetup || typeof viewSetup !== "object") return [];
  if (viewSetup.metadata || viewSetup.user) {
    return [viewSetup.metadata, viewSetup.user].filter(
      (s) =>
        s &&
        (Number(s.rotation_deg || s.rotationDeg) ||
          s.flip_h ||
          s.flipH ||
          s.flip_v ||
          s.flipV)
    );
  }
  if (
    Number(viewSetup.rotation_deg || viewSetup.rotationDeg) ||
    viewSetup.flip_h ||
    viewSetup.flipH ||
    viewSetup.flip_v ||
    viewSetup.flipV
  ) {
    return [viewSetup];
  }
  return [];
}

/**
 * True when analysis/display has an odd X-mirror (flip_h and/or 180°).
 * MediaPipe L/R on a mirrored frame often needs the opposite racket arm.
 */
export function viewSetupMirrorsX(viewSetup) {
  let mirrors = 0;
  for (const step of orientationSteps(viewSetup)) {
    if (step.flip_h || step.flipH) mirrors += 1;
    const rot = Number(step.rotation_deg ?? step.rotationDeg ?? 0) % 360;
    if (rot === 180) mirrors += 1;
  }
  return mirrors % 2 === 1;
}

/**
 * Anatomical racket arm for a court-side track, after mirror correction.
 * @returns {'left'|'right'}
 */
export function racketArmSide(courtSide, viewSetup = null) {
  // Court-left → right arm; court-right → left arm (unmirrored).
  let side = courtSide === "left" ? "right" : "left";
  if (viewSetupMirrorsX(viewSetup)) {
    side = side === "right" ? "left" : "right";
  }
  return side;
}

/**
 * Racket-arm elbow angle for a court-side player track.
 * @param {'left'|'right'} courtSide — sample.left / sample.right (court position)
 */
export function racketElbowDegrees(angles, courtSide, viewSetup = null) {
  if (!angles || typeof angles !== "object") return null;
  const arm = racketArmSide(courtSide, viewSetup);
  const key = arm === "right" ? "right_elbow" : "left_elbow";
  return numOrNull(angles[key]);
}

/**
 * Signed racket-shoulder open angle: elbow–shoulder–hip (same side).
 * Uses display-space joints so sign matches the oriented overlay (−180…180).
 */
export function racketShoulderOpenDegrees(joints, courtSide, viewSetup = null) {
  if (!Array.isArray(joints) || joints.length < 13) return null;
  const arm = racketArmSide(courtSide, viewSetup);
  const elbowIdx = arm === "right" ? R_ELBOW : L_ELBOW;
  const shoulderIdx = arm === "right" ? R_SHOULDER : L_SHOULDER;
  const hipIdx = arm === "right" ? R_HIP : L_HIP;
  const elbow = joints[elbowIdx];
  const shoulder = joints[shoulderIdx];
  const hip = joints[hipIdx];
  if (!jointOk(elbow) || !jointOk(shoulder) || !jointOk(hip)) return null;
  const de = mapStorageToDisplayNorm(Number(elbow.x), Number(elbow.y), viewSetup);
  const ds = mapStorageToDisplayNorm(Number(shoulder.x), Number(shoulder.y), viewSetup);
  const dh = mapStorageToDisplayNorm(Number(hip.x), Number(hip.y), viewSetup);
  return signedAngleDegreesAt(de, ds, dh);
}

/**
 * Shoulder-open angle for stroke detection — tolerates a weak/missing elbow
 * frame when shoulder + hip are still tracked (common during fast FH swings).
 */
export function racketShoulderOpenDegreesForDetection(
  joints,
  courtSide,
  viewSetup = null
) {
  const primary = racketShoulderOpenDegrees(joints, courtSide, viewSetup);
  if (primary != null) return primary;
  if (!Array.isArray(joints) || joints.length < 13) return null;

  const arm = racketArmSide(courtSide, viewSetup);
  const elbowIdx = arm === "right" ? R_ELBOW : L_ELBOW;
  const shoulderIdx = arm === "right" ? R_SHOULDER : L_SHOULDER;
  const hipIdx = arm === "right" ? R_HIP : L_HIP;
  const elbow = joints[elbowIdx];
  const shoulder = joints[shoulderIdx];
  const hip = joints[hipIdx];
  if (!jointOkRelaxed(elbow) || !jointOk(shoulder) || !jointOk(hip)) return null;

  const de = mapStorageToDisplayNorm(Number(elbow.x), Number(elbow.y), viewSetup);
  const ds = mapStorageToDisplayNorm(Number(shoulder.x), Number(shoulder.y), viewSetup);
  const dh = mapStorageToDisplayNorm(Number(hip.x), Number(hip.y), viewSetup);
  return signedAngleDegreesAt(de, ds, dh);
}

/**
 * Fill short null runs. Linear by default; when neighbors suggest a missing
 * peak tip (rising into the gap, falling out), place a synthetic peak mid-gap
 * instead of a chord that flattens the spike.
 * @param {number} maxGapMs max span from last good sample to next good sample
 */
export function fillShortGaps(vals, tMs, maxGapMs = 450, { preservePeaks = false } = {}) {
  const n = vals.length;
  if (n < 3) return vals.slice();
  const out = vals.slice();

  let i = 0;
  while (i < n) {
    if (Number.isFinite(out[i])) {
      i += 1;
      continue;
    }
    const gapStart = i;
    while (i < n && !Number.isFinite(out[i])) i += 1;
    const gapEnd = i;
    const leftI = gapStart - 1;
    const rightI = gapEnd;
    if (leftI < 0 || rightI >= n) continue;

    const leftV = out[leftI];
    const rightV = out[rightI];
    if (!Number.isFinite(leftV) || !Number.isFinite(rightV)) continue;

    const spanMs = (Number(tMs[rightI]) || 0) - (Number(tMs[leftI]) || 0);
    if (spanMs <= 0 || spanMs > maxGapMs) continue;

    let peakV = null;
    if (preservePeaks && leftI > 0 && rightI < n - 1) {
      const leftPrev = out[leftI - 1];
      const rightNext = out[rightI + 1];
      const risingIn =
        Number.isFinite(leftPrev) && leftV > leftPrev;
      const fallingOut =
        Number.isFinite(rightNext) && rightV > rightNext;
      if (risingIn && fallingOut) {
        const rise = leftV - leftPrev;
        const fall = rightV - rightNext;
        peakV = Math.max(leftV, rightV) + Math.max(rise, fall);
      }
    }

    const tLeft = Number(tMs[leftI]) || 0;
    const tRight = Number(tMs[rightI]) || 0;
    const tMid = (tLeft + tRight) / 2;
    for (let j = gapStart; j < gapEnd; j += 1) {
      const t = Number(tMs[j]) || 0;
      const alpha = (t - tLeft) / spanMs;
      if (peakV != null) {
        // Tent: rise left→peak then peak→right.
        if (t <= tMid) {
          const a = tMid === tLeft ? 0 : (t - tLeft) / (tMid - tLeft);
          out[j] = leftV + a * (peakV - leftV);
        } else {
          const a = tRight === tMid ? 1 : (t - tMid) / (tRight - tMid);
          out[j] = peakV + a * (rightV - peakV);
        }
      } else {
        out[j] = leftV + alpha * (rightV - leftV);
      }
    }
  }
  return out;
}

/**
 * Replace isolated 1–2 sample V-spikes that are uncorrelated with neighbors.
 * A bad elbow (76° → 6° → 155°) is interpolated; a real swing (20° → 90° → 150°) is kept.
 */
export function despikeIsolatedAngle(
  vals,
  tMs,
  { jumpDeg = 40, maxRunMs = 150, maxRun = 2 } = {}
) {
  const n = vals.length;
  if (n < 3) return vals.slice();
  const out = vals.slice();
  const finite = (v) => Number.isFinite(v);

  for (let run = 1; run <= maxRun; run += 1) {
    for (let i = 1; i + run < n; ) {
      const leftI = i - 1;
      const rightI = i + run;
      const left = out[leftI];
      const right = out[rightI];
      if (!finite(left) || !finite(right)) {
        i += 1;
        continue;
      }
      const dt = (Number(tMs[rightI]) || 0) - (Number(tMs[leftI]) || 0);
      if (!(dt > 0) || dt > maxRunMs) {
        i += 1;
        continue;
      }
      let isolated = true;
      for (let k = i; k < i + run; k += 1) {
        const cur = out[k];
        if (!finite(cur)) {
          isolated = false;
          break;
        }
        const localMin = cur < left && cur < right;
        const localMax = cur > left && cur > right;
        if (!localMin && !localMax) {
          isolated = false;
          break;
        }
        if (Math.abs(cur - left) < jumpDeg || Math.abs(cur - right) < jumpDeg) {
          isolated = false;
          break;
        }
      }
      if (!isolated) {
        i += 1;
        continue;
      }
      const tLeft = Number(tMs[leftI]) || 0;
      for (let k = i; k < i + run; k += 1) {
        const a = ((Number(tMs[k]) || 0) - tLeft) / dt;
        out[k] = left + a * (right - left);
      }
      i += run;
    }
  }
  return out;
}

/**
 * Signed horizontal shoulder delta in display-normalized coords:
 * racket_shoulder.x − other_shoulder.x (negative when racket is left of other on screen).
 */
export function shoulderSpanNorm(joints, courtSide, viewSetup = null) {
  if (!Array.isArray(joints) || joints.length < 7) return null;
  const arm = racketArmSide(courtSide, viewSetup);
  const racketIdx = arm === "right" ? R_SHOULDER : L_SHOULDER;
  const otherIdx = arm === "right" ? L_SHOULDER : R_SHOULDER;
  const a = joints[racketIdx];
  const b = joints[otherIdx];
  if (!jointOk(a) || !jointOk(b)) return null;
  const da = mapStorageToDisplayNorm(Number(a.x), Number(a.y), viewSetup);
  const db = mapStorageToDisplayNorm(Number(b.x), Number(b.y), viewSetup);
  return da.x - db.x;
}

/**
 * Euclidean L–R shoulder width in display-normalized coords (identity / depth cue).
 * Independent of which arm is the racket arm.
 */
export function shoulderWidthNorm(joints, viewSetup = null) {
  if (!Array.isArray(joints) || joints.length < 7) return null;
  const a = joints[L_SHOULDER];
  const b = joints[R_SHOULDER];
  if (!jointOk(a) || !jointOk(b)) return null;
  const da = mapStorageToDisplayNorm(Number(a.x), Number(a.y), viewSetup);
  const db = mapStorageToDisplayNorm(Number(b.x), Number(b.y), viewSetup);
  return Math.hypot(da.x - db.x, da.y - db.y);
}

export function racketElbowInteriorDegrees(joints, courtSide = "left", viewSetup = null) {
  if (!Array.isArray(joints) || joints.length < 11) return null;
  const arm = racketArmSide(courtSide, viewSetup);
  const shoulder = joints[arm === "right" ? R_SHOULDER : L_SHOULDER];
  const elbow = joints[arm === "right" ? R_ELBOW : L_ELBOW];
  const wrist = joints[arm === "right" ? R_WRIST : L_WRIST];
  if (!jointOk(shoulder) || !jointOk(elbow) || !jointOkRelaxed(wrist)) return null;
  const ds = mapStorageToDisplayNorm(Number(shoulder.x), Number(shoulder.y), viewSetup);
  const de = mapStorageToDisplayNorm(Number(elbow.x), Number(elbow.y), viewSetup);
  const dw = mapStorageToDisplayNorm(Number(wrist.x), Number(wrist.y), viewSetup);
  const ang = signedAngleDegreesAt(de, ds, dw);
  if (ang == null) {
    const atElbow = signedAngleDegreesAt(ds, de, dw);
    return atElbow == null ? null : Math.abs(atElbow);
  }
  return Math.abs(signedAngleDegreesAt(ds, de, dw));
}

/** Euclidean racket shoulder–wrist distance in display-normalized coords. */
export function racketShoulderWristDist(joints, courtSide = "left", viewSetup = null) {
  if (!Array.isArray(joints) || joints.length < 11) return null;
  const arm = racketArmSide(courtSide, viewSetup);
  const shoulder = joints[arm === "right" ? R_SHOULDER : L_SHOULDER];
  const wrist = joints[arm === "right" ? R_WRIST : L_WRIST];
  if (!jointOk(shoulder) || !jointOkRelaxed(wrist)) return null;
  const ds = mapStorageToDisplayNorm(Number(shoulder.x), Number(shoulder.y), viewSetup);
  const dw = mapStorageToDisplayNorm(Number(wrist.x), Number(wrist.y), viewSetup);
  return Math.hypot(dw.x - ds.x, dw.y - ds.y);
}

/** Wrist.x − elbow.x in display space (hand − elbow Δx). */
export function racketHandElbowDx(joints, courtSide = "left", viewSetup = null) {
  if (!Array.isArray(joints) || joints.length < 11) return null;
  const arm = racketArmSide(courtSide, viewSetup);
  const elbow = joints[arm === "right" ? R_ELBOW : L_ELBOW];
  const wrist = joints[arm === "right" ? R_WRIST : L_WRIST];
  if (!jointOk(elbow) || !jointOkRelaxed(wrist)) return null;
  const de = mapStorageToDisplayNorm(Number(elbow.x), Number(elbow.y), viewSetup);
  const dw = mapStorageToDisplayNorm(Number(wrist.x), Number(wrist.y), viewSetup);
  return dw.x - de.x;
}

/** Wrist.y − elbow.y in display space (positive = wrist below elbow). */
export function racketHandElbowDy(joints, courtSide = "left", viewSetup = null) {
  if (!Array.isArray(joints) || joints.length < 11) return null;
  const arm = racketArmSide(courtSide, viewSetup);
  const elbow = joints[arm === "right" ? R_ELBOW : L_ELBOW];
  const wrist = joints[arm === "right" ? R_WRIST : L_WRIST];
  if (!jointOk(elbow) || !jointOkRelaxed(wrist)) return null;
  const de = mapStorageToDisplayNorm(Number(elbow.x), Number(elbow.y), viewSetup);
  const dw = mapStorageToDisplayNorm(Number(wrist.x), Number(wrist.y), viewSetup);
  return dw.y - de.y;
}

/**
 * Prep pose for FH topspin: racket wrist left of the (racket-side) knee
 * and below the hip line, in display space (y down).
 */
export function racketPrepTopspinCues(joints, courtSide = "left", viewSetup = null) {
  if (!Array.isArray(joints) || joints.length < 15) return null;
  const arm = racketArmSide(courtSide, viewSetup);
  const wrist = joints[arm === "right" ? R_WRIST : L_WRIST];
  if (!jointOkRelaxed(wrist)) return null;
  const dw = mapStorageToDisplayNorm(Number(wrist.x), Number(wrist.y), viewSetup);
  let knee = joints[arm === "right" ? R_KNEE : L_KNEE];
  if (!jointOkRelaxed(knee)) {
    knee = joints[arm === "right" ? L_KNEE : R_KNEE];
  }
  const kneePt = jointOkRelaxed(knee)
    ? mapStorageToDisplayNorm(Number(knee.x), Number(knee.y), viewSetup)
    : null;
  const hips = [];
  for (const idx of [L_HIP, R_HIP]) {
    const h = joints[idx];
    if (jointOkRelaxed(h)) {
      hips.push(mapStorageToDisplayNorm(Number(h.x), Number(h.y), viewSetup));
    }
  }
  if (!hips.length) return null;
  const hipY = hips.reduce((s, p) => s + p.y, 0) / hips.length;
  return {
    left_of_knee: kneePt ? dw.x < kneePt.x : null,
    below_hips: dw.y > hipY,
    wrist_x: dw.x,
    wrist_y: dw.y,
    knee_x: kneePt ? kneePt.x : null,
    hip_y: hipY,
  };
}

/**
 * Toss-hand wrist (opposite the racket arm), storage-normalized — same space as serve detection.
 */
export function tossWristNorm(joints, courtSide = "left", viewSetup = null) {
  if (!Array.isArray(joints) || joints.length < 11) return null;
  const racket = racketArmSide(courtSide, viewSetup);
  const wrist = joints[racket === "right" ? L_WRIST : R_WRIST];
  if (!jointOkRelaxed(wrist)) return null;
  return { x: Number(wrist.x), y: Number(wrist.y) };
}

/**
 * Wrist vs elbow geometry in display space.
 * @returns {{ below: boolean, left: boolean, above: boolean, right: boolean }|null}
 */
export function racketWristElbowFlags(joints, courtSide = "left", viewSetup = null) {
  if (!Array.isArray(joints) || joints.length < 11) return null;
  const arm = racketArmSide(courtSide, viewSetup);
  const elbow = joints[arm === "right" ? R_ELBOW : L_ELBOW];
  const wrist = joints[arm === "right" ? R_WRIST : L_WRIST];
  if (!jointOk(elbow) || !jointOkRelaxed(wrist)) return null;
  const de = mapStorageToDisplayNorm(Number(elbow.x), Number(elbow.y), viewSetup);
  const dw = mapStorageToDisplayNorm(Number(wrist.x), Number(wrist.y), viewSetup);
  return {
    below: dw.y > de.y,
    left: dw.x < de.x,
    above: dw.y < de.y,
    right: dw.x > de.x,
  };
}

function chartRowFromSample(s, viewSetup) {
  const tossA = tossWristNorm(s.left?.joints, "left", viewSetup);
  const tossB = tossWristNorm(s.right?.joints, "right", viewSetup);
  return {
    tSec: (Number(s.t_ms) || 0) / 1000,
    tMs: Number(s.t_ms) || 0,
    playerARacketShoulder: racketShoulderOpenDegreesForDetection(
      s.left?.joints,
      "left",
      viewSetup
    ),
    playerAShoulderSpan: shoulderSpanNorm(s.left?.joints, "left", viewSetup),
    playerAHandElbowDx: racketHandElbowDx(s.left?.joints, "left", viewSetup),
    playerAHandElbowDy: racketHandElbowDy(s.left?.joints, "left", viewSetup),
    playerAShoulderWrist: racketShoulderWristDist(
      s.left?.joints,
      "left",
      viewSetup
    ),
    playerARacketElbowInterior: racketElbowInteriorDegrees(
      s.left?.joints,
      "left",
      viewSetup
    ),
    playerATossWristX: tossA?.x ?? null,
    playerATossWristY: tossA?.y ?? null,
    playerBTossWristX: tossB?.x ?? null,
    playerBTossWristY: tossB?.y ?? null,
  };
}

export function buildPoseAngleChartData(
  samples,
  viewSetup = null,
  { maxPoints = 900 } = {}
) {
  if (!Array.isArray(samples) || samples.length === 0) return [];
  const full = samples.map((s) => chartRowFromSample(s, viewSetup));
  const tMs = full.map((r) => r.tMs);
  const ang = despikeIsolatedAngle(
    full.map((r) => r.playerARacketShoulder),
    tMs
  );
  for (let i = 0; i < full.length; i += 1) {
    full[i].playerARacketShoulder = ang[i];
  }
  // Downsample long phone/server scans so Recharts doesn't freeze the main thread.
  const max = Math.max(120, Number(maxPoints) || 900);
  if (full.length <= max) return full;
  const step = Math.ceil(full.length / max);
  const rows = [];
  for (let i = 0; i < full.length; i += step) {
    rows.push(full[i]);
  }
  const last = full[full.length - 1];
  if (!rows.length || rows[rows.length - 1].tMs !== last.tMs) {
    rows.push(last);
  }
  return rows;
}

export function angleDomain(values, { pad = 8, minSpan = 30, allowNegative = false } = {}) {
  const nums = values.filter((v) => Number.isFinite(v));
  if (!nums.length) return allowNegative ? [-90, 180] : [0, 180];
  let lo = Math.min(...nums);
  let hi = Math.max(...nums);
  lo = lo - pad;
  hi = hi + pad;
  if (allowNegative) {
    lo = Math.max(-180, lo);
    hi = Math.min(180, hi);
  } else {
    lo = Math.max(0, lo);
    hi = Math.min(180, hi);
  }
  if (hi - lo < minSpan) {
    const mid = (hi + lo) / 2;
    lo = mid - minSpan / 2;
    hi = mid + minSpan / 2;
    if (allowNegative) {
      lo = Math.max(-180, lo);
      hi = Math.min(180, hi);
    } else {
      lo = Math.max(0, lo);
      hi = Math.min(180, hi);
    }
  }
  // Keep 0° visible when the series can cross (signed shoulder open).
  if (allowNegative) {
    if (lo > 0) lo = Math.min(lo, 0);
    if (hi < 0) hi = Math.max(hi, 0);
  }
  return [Math.round(lo), Math.round(hi)];
}

/** Domain for signed numeric series (e.g. shoulder x delta); allows negatives. */
export function numericDomain(values, { padRatio = 0.08, minSpan = 0.04 } = {}) {
  const nums = values.filter((v) => Number.isFinite(v));
  if (!nums.length) return [-0.2, 0.2];
  let lo = Math.min(...nums);
  let hi = Math.max(...nums);
  const pad = Math.max((hi - lo) * padRatio, minSpan * 0.15);
  lo = lo - pad;
  hi = hi + pad;
  if (hi - lo < minSpan) {
    const mid = (hi + lo) / 2;
    lo = mid - minSpan / 2;
    hi = mid + minSpan / 2;
  }
  // Keep zero in view when signs cross or data is one-sided near zero.
  if (lo > 0) lo = Math.min(lo, 0);
  if (hi < 0) hi = Math.max(hi, 0);
  return [Number(lo.toFixed(3)), Number(hi.toFixed(3))];
}
